import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
  DialogFormFieldsSkeleton,
} from '@shared/ui';
import { validateCreateOperationForm } from '@features/service-accounting';
import {
  useCreateAccountingOperation,
  type CreateOperationFormValues,
} from '@features/service-accounting';
import { useWalletsForOperation } from '@features/service-accounting';
import { SERVICE_OPERATION_TYPES } from '@entities/service-accounting';
import { OperationWalletSelect } from './operation-wallet-select';

interface CreateOperationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const EMPTY_FORM: CreateOperationFormValues = {
  sum: '',
  walletId: '',
  invoiceId: '',
  comment: '',
  type: 'adjustment',
  assetLabel: '',
  relatedOperationIds: '',
};

type OperationFormField = keyof CreateOperationFormValues;

export function CreateOperationModal({ open, onOpenChange, onSuccess }: CreateOperationModalProps) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { createOperation, isLoading, clearError } = useCreateAccountingOperation();
  const [walletSearch, setWalletSearch] = useState('');
  const {
    wallets,
    isInitialLoading: isWalletsInitialLoading,
    isListLoading: isWalletsListLoading,
    isLoadingMore: isWalletsLoadingMore,
    hasMore: hasMoreWallets,
    loadMore: loadMoreWallets,
  } = useWalletsForOperation(open, walletSearch);
  const [form, setForm] = useState<CreateOperationFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setWalletSearch('');
    setErrors({});
    setSubmitted(false);
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  const revalidateField = (field: OperationFormField, nextValues: CreateOperationFormValues) => {
    if (!submitted) return;

    const validationErrors = validateCreateOperationForm(nextValues, t);
    setErrors((prev) => {
      const next = { ...prev };
      if (validationErrors[field]) {
        next[field] = validationErrors[field];
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const updateField = <K extends OperationFormField>(field: K, value: CreateOperationFormValues[K]) => {
    const next = { ...form, [field]: value };
    setForm(next);
    if (submitted) {
      if (field === 'type') {
        setErrors(validateCreateOperationForm(next, t));
      } else {
        revalidateField(field, next);
      }
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);

    const validationErrors = validateCreateOperationForm(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    const result = await createOperation(form);
    if (result.success) {
      toast(t('common.success', { ns: 'common' }), 'success');
      onOpenChange(false);
      onSuccess?.();
      return;
    }

    if (result.error) {
      toast(result.error, 'error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-none overflow-visible overflow-y-visible sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('serviceAccounting.modal.title')}</DialogTitle>
          <DialogDescription>{t('serviceAccounting.modal.description')}</DialogDescription>
        </DialogHeader>

        {open && isWalletsInitialLoading ? (
          <DialogFormFieldsSkeleton fields={5} />
        ) : (
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="sa-type">{t('serviceAccounting.modal.typeLabel')}</Label>
            <Select
              value={form.type}
              onValueChange={(value) => updateField('type', value as CreateOperationFormValues['type'])}
            >
              <SelectTrigger id="sa-type">
                <SelectValue placeholder={t('serviceAccounting.modal.typePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_OPERATION_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`serviceAccounting.types.${type}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="sa-sum">{t('serviceAccounting.modal.sumLabel')}</Label>
            <Input
              id="sa-sum"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder={t('serviceAccounting.modal.sumPlaceholder')}
              value={form.sum}
              onChange={(event) => updateField('sum', event.target.value)}
              error={errors.sum}
            />
          </div>

          <OperationWalletSelect
            id="sa-walletId"
            panelOpen={open}
            wallets={wallets}
            value={form.walletId}
            onChange={(walletId) => updateField('walletId', walletId)}
            search={walletSearch}
            onSearchChange={setWalletSearch}
            isListLoading={isWalletsListLoading}
            isLoadingMore={isWalletsLoadingMore}
            hasMore={hasMoreWallets}
            onLoadMore={() => void loadMoreWallets()}
            error={errors.walletId}
          />

          {form.type === 'expense' && (
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="sa-assetLabel">{t('serviceAccounting.modal.assetLabel')}</Label>
              <Input
                id="sa-assetLabel"
                placeholder={t('serviceAccounting.modal.assetLabelPlaceholder')}
                value={form.assetLabel}
                onChange={(event) => updateField('assetLabel', event.target.value)}
                error={errors.assetLabel}
              />
            </div>
          )}

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="sa-invoiceId">{t('serviceAccounting.modal.invoiceLabel')}</Label>
            <Input
              id="sa-invoiceId"
              placeholder={t('serviceAccounting.modal.invoicePlaceholder')}
              value={form.invoiceId}
              onChange={(event) => updateField('invoiceId', event.target.value)}
              error={errors.invoiceId}
            />
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="sa-relatedOperationIds">{t('serviceAccounting.modal.relatedLabel')}</Label>
            <Input
              id="sa-relatedOperationIds"
              placeholder={t('serviceAccounting.modal.relatedPlaceholder')}
              value={form.relatedOperationIds}
              onChange={(event) => updateField('relatedOperationIds', event.target.value)}
            />
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="sa-comment">{t('serviceAccounting.modal.commentLabel')}</Label>
            <Textarea
              id="sa-comment"
              placeholder={t('serviceAccounting.modal.commentPlaceholder')}
              value={form.comment}
              onChange={(event) => updateField('comment', event.target.value)}
              error={errors.comment}
            />
          </div>

          <DialogFooter className="px-0 pt-0">
            <Button type="submit" variant="default" loading={isLoading} disabled={isWalletsInitialLoading}>
              {t('serviceAccounting.modal.submit')}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
